/**
 * PrimaryActionBar — iter189.
 * Per-group-type primary action chips that sit above the Tabs.
 * Different group types surface different "most-used" actions so members
 * don't have to dig into the FAB for the #1 thing they do.
 *
 *   sports → I'm in (RSVP) · Add expense · Charge fee (admin) · Settle
 *   trip   → Add expense · Trip dashboard · Settle · My ledger
 *   daily  → Add expense · Settle · Top up fund · My ledger
 *   event  → Add expense · Settle · Top up · My ledger
 *
 * Hidden when ctx is incomplete. Keeps tab list clean for power users
 * who already know where things are.
 */
import { Plus, BookOpen, Zap, BarChart3, Flame, CheckCircle2, ListPlus, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PrimaryActionBar({
  typeConfig,
  group,
  isAdmin,
  currentUserId,
  onAddExpense,
  onSettle,
  onTopUp,
  onMyLedger,
  onChargeFee,
  onIamIn,
  onManage,            // iter216 — opens the Admin Actions sheet (replaces bottom FAB)
  rsvpState,           // 'in' | 'out' | 'pending'
  nextSessionId,
}) {
  const navigate = useNavigate();
  if (!typeConfig || !group) return null;

  // iter193 — Hide write-action chips on archived trips. Settlements still
  // allowed → keep "Settle" + "My ledger" but drop "Add expense", "Top up",
  // "I'm in", "Charge fee" since they'd 403 server-side anyway.
  const isArchived = !!group?.archived_at;

  // Define the chip list per type. Order matters — leftmost = highest priority.
  let chips = [];
  if (typeConfig.isSports) {
    chips = [
      // RSVP "I'm in" only when there's a session to RSVP to
      !isArchived && nextSessionId && {
        key: 'iam-in',
        label: rsvpState === 'in' ? "I'm in ✓" : "I'm in",
        Icon: CheckCircle2,
        tone: rsvpState === 'in' ? 'success' : 'brand',
        onClick: onIamIn,
        testId: 'action-iam-in',
      },
      !isArchived && { key: 'add-expense', label: 'Add expense', Icon: Plus, tone: 'brand', onClick: onAddExpense, testId: 'action-add-expense' },
      !isArchived && isAdmin && { key: 'charge-fee', label: 'Charge fee', Icon: Flame, tone: 'amber', onClick: onChargeFee, testId: 'action-charge-fee' },
      { key: 'settle', label: 'Settle', Icon: Zap, tone: 'neutral', onClick: onSettle, testId: 'action-settle' },
      isAdmin && onManage && { key: 'manage', label: 'Manage', Icon: Settings, tone: 'neutral', onClick: onManage, testId: 'action-manage' },
    ].filter(Boolean);
  } else if (typeConfig.isTrip) {
    chips = [
      !isArchived && { key: 'add-expense', label: 'Add expense', Icon: Plus, tone: 'brand', onClick: onAddExpense, testId: 'action-add-expense' },
      {
        key: 'trip-dashboard',
        label: 'Trip dashboard',
        Icon: BarChart3,
        tone: 'brand',
        onClick: () => navigate(`/groups/${group.id}/trip-summary`),
        testId: 'action-trip-dashboard',
      },
      { key: 'settle', label: 'Settle', Icon: Zap, tone: 'neutral', onClick: onSettle, testId: 'action-settle' },
      currentUserId && {
        key: 'my-ledger',
        label: 'My ledger',
        Icon: BookOpen,
        tone: 'neutral',
        onClick: () => onMyLedger?.(currentUserId),
        testId: 'action-my-ledger',
      },
      isAdmin && onManage && { key: 'manage', label: 'Manage', Icon: Settings, tone: 'neutral', onClick: onManage, testId: 'action-manage' },
    ].filter(Boolean);
  } else {
    // daily / event: same baseline
    chips = [
      !isArchived && { key: 'add-expense', label: 'Add expense', Icon: Plus, tone: 'brand', onClick: onAddExpense, testId: 'action-add-expense' },
      { key: 'settle', label: 'Settle', Icon: Zap, tone: 'neutral', onClick: onSettle, testId: 'action-settle' },
      currentUserId && {
        key: 'my-ledger',
        label: 'My ledger',
        Icon: BookOpen,
        tone: 'neutral',
        onClick: () => onMyLedger?.(currentUserId),
        testId: 'action-my-ledger',
      },
      isAdmin && onManage && { key: 'manage', label: 'Manage', Icon: Settings, tone: 'neutral', onClick: onManage, testId: 'action-manage' },
    ].filter(Boolean);
  }

  if (chips.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-2 pb-1 mb-3"
      data-testid="primary-action-bar"
    >
      {chips.map((c) => (
        <ChipButton key={c.key} chip={c} />
      ))}
    </div>
  );
}

function ChipButton({ chip }) {
  const toneClass = {
    brand:   'bg-primary text-primary-foreground hover:opacity-90',
    success: 'bg-emerald-600 text-white hover:bg-emerald-700',
    amber:   'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-2 border-amber-500/40 hover:bg-amber-500/25',
    neutral: 'bg-card border-2 border-border hover:bg-accent text-foreground',
  }[chip.tone] || 'bg-card border-2 border-border';

  const Icon = chip.Icon || ListPlus;
  return (
    <button
      type="button"
      onClick={chip.onClick}
      data-testid={chip.testId}
      className={`shrink-0 rounded-full px-3.5 h-10 flex items-center gap-1.5 text-xs font-bold tracking-tight active:scale-95 transition ${toneClass}`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
      <span>{chip.label}</span>
    </button>
  );
}
